import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import {
  BookOpenIcon,
  ExclamationTriangleIcon,
  ClipboardDocumentCheckIcon,
  IdentificationIcon,
} from '@heroicons/react/24/outline'
import apiClient from '../../services/apiClient'
import CourseReviewModal from '../../components/learning/CourseReviewModal'
import BaProcessFlowCarousel from '../../components/course/BaProcessFlowCarousel'
import { isAi4baCourseCode } from '../../data/ai4baBaProcessFlows'
import CourseDetailHeader from '../../components/student/course-detail/CourseDetailHeader'
import ClassRegistrationInfo from '../../components/student/course-detail/ClassRegistrationInfo'
import SessionScheduleGrid from '../../components/student/course-detail/SessionScheduleGrid'
import {
  ClassroomSessionItem,
  courseDetailStatusLabel,
  isLiveMethod,
  isSessionCompleted,
  pickNextSession,
  StudentCourseDetailData,
} from '../../components/student/course-detail/types'

function stripHtmlEntities(text: string): string {
  return text
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .trim()
}

/** Parse nội quy HTML / plain text → danh sách ý để render icon tròn. */
function parseRuleItems(raw: string): string[] {
  if (!raw?.trim()) return []
  const liMatches = [...raw.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi)]
  if (liMatches.length > 0) {
    return liMatches
      .map((m) => stripHtmlEntities(m[1].replace(/<[^>]+>/g, '')))
      .filter(Boolean)
  }
  return raw
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .split(/\r?\n/)
    .map((line) => stripHtmlEntities(line).replace(/^[\s•\-*]+/, ''))
    .filter(Boolean)
}

const StudentCourseDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const [data, setData] = useState<StudentCourseDetailData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reviewModalOpen, setReviewModalOpen] = useState(false)
  const [hasReview, setHasReview] = useState(false)
  const [myRate, setMyRate] = useState<number | null>(null)
  const [sessions, setSessions] = useState<ClassroomSessionItem[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(false)

  const fetchMyReview = useCallback(async (courseId: number) => {
    try {
      const res = await apiClient.get(`/student/courses/${courseId}/review`)
      const review = res.data?.data
      if (review?.id) {
        setHasReview(true)
        setMyRate(typeof review.rate === 'number' ? review.rate : null)
      } else {
        setHasReview(false)
        setMyRate(null)
      }
    } catch {
      setHasReview(false)
      setMyRate(null)
    }
  }, [])

  useEffect(() => {
    const fetchCourseDetail = async () => {
      try {
        const response = await apiClient.get(`/student/courses/${id}`)
        if (response.data.success) {
          setData(response.data.data)
          const courseId = response.data.data?.courseInfo?.id
          if (courseId) {
            await fetchMyReview(courseId)
          }
        } else {
          setError(response.data.message)
        }
      } catch (err: any) {
        setError(err.response?.data?.message || 'Có lỗi xảy ra khi tải dữ liệu')
      } finally {
        setLoading(false)
      }
    }

    fetchCourseDetail()
  }, [id, fetchMyReview])

  useEffect(() => {
    if (!data?.courseInfo?.id || !isLiveMethod(data.courseInfo.method) || !data.classroomInfo) {
      setSessions([])
      return
    }

    let cancelled = false
    const fetchSessions = async () => {
      setSessionsLoading(true)
      try {
        const response = await apiClient.get(
          `/student/courses/${data.courseInfo.id}/classroom-sessions`
        )
        if (cancelled) return
        if (response.data.success) {
          const mySessions: ClassroomSessionItem[] =
            response.data.data?.myClassroom?.sessions || []
          setSessions(mySessions)
        } else {
          setSessions([])
        }
      } catch {
        if (!cancelled) setSessions([])
      } finally {
        if (!cancelled) setSessionsLoading(false)
      }
    }

    void fetchSessions()
    return () => {
      cancelled = true
    }
  }, [data])

  const nextSession = useMemo(() => pickNextSession(sessions), [sessions])
  const completedCount = useMemo(
    () => sessions.filter((s) => isSessionCompleted(s)).length,
    [sessions]
  )

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-m2-surface-low">
        <p className="text-m2-on-surface-variant text-lg">Đang tải dữ liệu...</p>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-m2-surface-low">
        <p className="mb-4 text-xl font-medium text-m2-error">
          {error || 'Không tìm thấy khóa học'}
        </p>
        <Link to="/student/my-courses" className="text-eh-forest-ink hover:underline">
          Quay lại danh sách khóa học
        </Link>
      </div>
    )
  }

  const { courseInfo, classroomInfo, studentInfo, registrationInfo } = data
  const live = isLiveMethod(courseInfo.method)
  const statusLabel = courseDetailStatusLabel(
    registrationInfo?.status || studentInfo.status,
    classroomInfo
  )

  return (
    <div className="min-h-screen bg-eh-cream pb-12">
      <CourseDetailHeader
        courseId={courseInfo.id}
        courseName={courseInfo.name}
        method={courseInfo.method}
        statusLabel={statusLabel}
        classroomName={classroomInfo?.name}
        startDate={classroomInfo?.startDate}
        nextSession={nextSession}
        classroomMeetLink={classroomInfo?.googleMeetLink}
        hasReview={hasReview}
        myRate={myRate}
        onOpenReview={() => setReviewModalOpen(true)}
        showZaloPanel={!!classroomInfo && (live || !!classroomInfo.zaloLink)}
        zaloLink={classroomInfo?.zaloLink}
        completedCount={completedCount}
        totalCount={sessions.length}
      />

      <div className="eh-container mt-8 space-y-8">
        {/* Thông tin lớp & đăng ký — full width (Zalo đã lên header) */}
        <ClassRegistrationInfo
          classroomInfo={classroomInfo}
          registrationInfo={registrationInfo}
          studentRegisteredAt={studentInfo.registeredAt}
          method={courseInfo.method}
          expireDate={studentInfo.expireDate}
        />

        {/* Hàng 2: Lịch buổi học full width */}
        {live && classroomInfo && (
          <SessionScheduleGrid
            sessions={sessions}
            classroomMeetLink={classroomInfo.googleMeetLink}
            loading={sessionsLoading}
          />
        )}

        {/* Hàng 3: Nội dung / nội quy (secondary) */}
        <div className="space-y-8">
          {isAi4baCourseCode(courseInfo.code) && (
            <div className="rounded-2xl border-2 border-m2-primary/35 bg-m2-surface-container p-6 sm:p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-m2-on-surface mb-2 flex items-center">
                <BookOpenIcon className="w-6 h-6 mr-3 text-m2-primary shrink-0" />
                Bộ Skill theo quy trình
              </h2>
              <p className="text-m2-on-surface-variant mb-6 text-sm sm:text-base">
                Workflow E2E — nối task thành hệ thống AI bạn điều phối. Bộ skill và workflow
                thực hiện full quy trình công việc BA.
              </p>
              <BaProcessFlowCarousel />
            </div>
          )}

          {courseInfo.requiredInput && (
            <div className="bg-m2-surface-container border border-m2-outline-variant rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-m2-on-surface mb-6 flex items-center">
                <IdentificationIcon className="w-6 h-6 mr-3 text-m2-primary" /> Yêu cầu
              </h2>
              <div
                className="prose prose-invert max-w-none prose-p:text-m2-on-surface-variant prose-li:text-m2-on-surface-variant prose-strong:text-m2-on-surface"
                dangerouslySetInnerHTML={{ __html: courseInfo.requiredInput }}
              />
            </div>
          )}

          {courseInfo.rule && (
            <div className="bg-m2-surface-container border border-m2-outline-variant rounded-2xl p-8 shadow-sm">
              <h2 className="text-2xl font-bold text-m2-on-surface mb-6 flex items-center">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-m2-primary/15 text-m2-primary mr-3 shrink-0">
                  <ClipboardDocumentCheckIcon className="h-5 w-5" />
                </span>
                Nội quy lớp học
              </h2>
              {(() => {
                const ruleItems = parseRuleItems(courseInfo.rule)
                if (ruleItems.length === 0) {
                  return (
                    <div
                      className="text-m2-on-surface leading-relaxed [&_li]:text-m2-on-surface [&_p]:text-m2-on-surface [&_ul]:list-none [&_ul]:pl-0"
                      dangerouslySetInnerHTML={{ __html: courseInfo.rule }}
                    />
                  )
                }
                return (
                  <ul className="space-y-3">
                    {ruleItems.map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-3 rounded-xl border border-m2-outline-variant bg-m2-surface-low p-4 text-m2-on-surface"
                      >
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-m2-primary/15 text-m2-primary mt-0.5">
                          <ExclamationTriangleIcon className="h-4 w-4" aria-hidden />
                        </span>
                        <span className="text-[15px] leading-snug pt-1">{item}</span>
                      </li>
                    ))}
                  </ul>
                )
              })()}
            </div>
          )}
        </div>
      </div>

      <CourseReviewModal
        isOpen={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        courseId={courseInfo.id}
        courseName={courseInfo.name}
        onSaved={() => {
          setHasReview(true)
          void fetchMyReview(courseInfo.id)
        }}
      />
    </div>
  )
}

export default StudentCourseDetailPage
